import {
    IMAGE_PRESET_WIDTHS,
    MIN_IMAGE_WIDTH,
    nearestImageGrid,
    SingleImageSize,
} from '../../../../../pdf-renderer/image-size';
import { SIDE_MAX_IMAGE_WIDTH } from '../../../../../pdf-renderer/caption-style';

/**
 * Tamanho da imagem única: `grid` é o tamanho pronto (1 grande, 2 média, 3 pequena) e
 * `width` a largura livre em % da coluna, que vence o `grid` quando existe.
 */
export type ImageSize = SingleImageSize;

/** Distância, em pontos percentuais, em que o arraste gruda num tamanho pronto. */
const SNAP = 2;

/**
 * Largura digitada (ou do − / +) como tamanho: 100 é a imagem grande; abaixo disso é
 * largura livre, sem grudar nos prontos (quem digita quer o número exato).
 */
export function imageSizeFromWidth(width: number): ImageSize {
    const clamped = Math.min(100, Math.max(MIN_IMAGE_WIDTH, Math.round(width)));
    if (clamped >= 100) return { grid: 1, width: null };
    return { grid: nearestImageGrid(clamped), width: clamped };
}

/**
 * Largura do arraste como tamanho: perto de um pronto (ou no limite da coluna) vira ele;
 * fora disso, largura livre inteira com o pronto mais perto junto.
 */
export function snapImageSize(rawWidth: number): ImageSize {
    const clamped = Math.min(100, Math.max(MIN_IMAGE_WIDTH, rawWidth));

    const preset = [1, 2, 3].find(grid => Math.abs(clamped - IMAGE_PRESET_WIDTHS[grid]) <= SNAP);
    if (preset) return { grid: preset, width: null };

    return imageSizeFromWidth(clamped);
}

/**
 * Com a legenda ao lado, a largura (em % da coluna) de cada tamanho pronto: a grande com 60%
 * do espaço sem o vão de 16px, a média com metade, a pequena com um terço (como no PDF).
 */
const SIDE_PRESET_WIDTHS: Record<number, number> = { 1: 59.4, 2: 48.75, 3: 31.67 };

/**
 * Largura do arraste com a legenda ao lado: perto de um pronto (na régua de ao lado) vira ele;
 * de 60% para cima é a grande; fora disso, largura livre.
 */
export function snapSideImageSize(rawWidth: number): ImageSize {
    const clamped = Math.min(SIDE_MAX_IMAGE_WIDTH, Math.max(MIN_IMAGE_WIDTH, rawWidth));

    const preset = [1, 2, 3].find(grid => Math.abs(clamped - SIDE_PRESET_WIDTHS[grid]) <= SNAP);
    if (preset) return { grid: preset, width: null };
    if (clamped >= SIDE_MAX_IMAGE_WIDTH) return { grid: 1, width: null };

    return imageSizeFromWidth(clamped);
}

/** Largura em % que o tamanho ocupa (o campo mostra isso também nos prontos). */
export function imageSizeWidth(size: ImageSize): number {
    return size.width ?? Math.round(IMAGE_PRESET_WIDTHS[size.grid] ?? 100);
}

/** − e +: de 5 em 5, parando nos múltiplos de 5 (49 + vira 50, não 54). */
export function stepImageWidth(width: number, direction: 1 | -1): number {
    const next =
        direction > 0 ? Math.floor(width / 5) * 5 + 5 : Math.ceil(width / 5) * 5 - 5;
    return Math.min(100, Math.max(MIN_IMAGE_WIDTH, next));
}

/** Mesmo tamanho? (largura livre compara a largura; pronto compara o pronto). */
export function sameImageSize(a: ImageSize, b: ImageSize): boolean {
    return a.width !== null || b.width !== null ? a.width === b.width : a.grid === b.grid;
}

/** Texto digitado como largura (só dígitos; vazio ou lixo = null). */
export function parseImageWidthInput(raw: string): number | null {
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) return null;
    const value = Number(digits);
    return Number.isFinite(value) ? value : null;
}
